import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { eventsAPI } from '../../api/events';
import { customEventsApi } from '../../api/customEvents';
import { getTradeSignalAnalysis } from '../../api/tradeSignals';
import userTradeSignalsAPI from '../../api/userTradeSignals';
import { getEventTypeLabel } from '../../constants/eventTypes';
import DWLFLoader from '../../components/DWLFLoader';
import PageHeader from '../../components/PageHeader';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'system', label: 'System' },
  { value: 'custom', label: 'Custom' },
  { value: 'strategy', label: 'Strategy' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'signal', label: 'signal' },
  { value: 'event', label: 'event' },
  { value: 'alert', label: 'alert' },
];

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today', days: 1 },
  { value: '7d', label: '7D', days: 7 },
  { value: '30d', label: '30D', days: 30 },
];

const DEFAULT_FILTERS = {
  source: 'all',
  type: 'all',
  timeRange: '7d',
  symbol: '',
};

const formatDateTime = (value) => {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTimestampValue = (value) => {
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getRangeStart = (range) => {
  const now = new Date();
  if (range === 'today') {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  }

  const selected = RANGE_OPTIONS.find((option) => option.value === range);
  const days = selected?.days ?? 7;
  return now.getTime() - days * 24 * 60 * 60 * 1000;
};

const normalizeEventItems = (events = []) =>
  events.map((event, index) => {
    const timestamp = event.eventTimestamp || event.date || event.createdAt;
    return {
      id: `event-${event.eventId || event.id || event.symbol || index}-${timestamp || index}`,
      source: 'system',
      sourceLabel: 'System',
      type: 'event',
      timestamp,
      symbol: event.symbol || '',
      title: event.eventType === 'custom_event'
        ? event.eventName || 'Custom Event Triggered'
        : getEventTypeLabel(event.eventType),
      description: event.details || event.description || event.eventName || getEventTypeLabel(event.eventType),
      detailPath: event.symbol ? `/markets/${event.symbol.replace('/', '-')}` : '/signals/events',
      sourcePath: '/signals/events',
    };
  });

const normalizeCustomEventItems = (notifications = []) =>
  (Array.isArray(notifications) ? notifications : []).map((n, index) => ({
    id: `custom-${n.customEventId || n.id || index}-${n.date || index}`,
    source: 'custom',
    sourceLabel: 'Custom',
    type: 'alert',
    timestamp: n.date || n.triggeredAt || n.createdAt,
    symbol: n.symbol || '',
    title: n.eventName || n.name || 'Custom Event',
    description: n.description || `Custom event triggered${n.symbol ? ` on ${n.symbol}` : ''}`,
    detailPath: n.symbol ? `/markets/${n.symbol.replace('/', '-')}` : '/custom-events',
    sourcePath: '/custom-events',
  }));

const normalizeSystemSignalItems = (signals = []) =>
  signals.map((signal, index) => {
    const timestamp = signal.date || signal.createdAt || signal.updatedAt;
    return {
      id: `signal-system-${signal.signalId || signal.id || signal.contextHash || index}`,
      source: 'system',
      sourceLabel: 'System',
      type: 'signal',
      timestamp,
      symbol: signal.symbol || '',
      title: signal.strategy || 'System Trade Signal',
      description: signal.reasonSummary || signal.reason || 'System-generated trade signal',
      detailPath: signal.symbol ? `/markets/${signal.symbol.replace('/', '-')}` : '/my-signals',
      sourcePath: '/my-signals',
    };
  });

const normalizeStrategySignalItems = (signals = []) =>
  signals.map((signal, index) => {
    const timestamp = signal.date || signal.createdAt || signal.updatedAt;
    return {
      id: `signal-strategy-${signal.signalId || signal.id || signal.contextHash || index}`,
      source: 'strategy',
      sourceLabel: 'Strategy',
      type: 'signal',
      timestamp,
      symbol: signal.symbol || '',
      title: signal.strategyDescription || signal.strategy || 'Strategy Signal',
      description: signal.reasonSummary || signal.reason || 'Signal from your configured strategy',
      detailPath: signal.symbol ? `/markets/${signal.symbol.replace('/', '-')}` : '/my-signals',
      sourcePath: '/my-signals',
    };
  });

const ActivityFeedPage = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAllActivity = useCallback(async () => {
    setLoading(true);
    setError('');

    // Fetch each source independently so one failure doesn't break the whole feed
    const [systemEventsResult, customEventsResult, systemSignalsResult, userSignalsResult] = await Promise.allSettled([
      eventsAPI.getRecentEvents({
        scope: 'system',
        days: 30,
        limit: 250,
        sortOrder: 'desc',
      }),
      customEventsApi.getNotifications(30),  // actual triggered occurrences, not definitions
      getTradeSignalAnalysis({
        limit: 250,
        offset: 0,
        sortOrder: 'desc',
      }),
      userTradeSignalsAPI.getUserTradeSignals({
        limit: 250,
        offset: 0,
        sortOrder: 'desc',
      }),
    ]);

    const failures = [systemEventsResult, customEventsResult, systemSignalsResult, userSignalsResult]
      .filter((r) => r.status === 'rejected').length;
    if (failures === 4) {
      setError('Failed to load activity feed');
      setLoading(false);
      return;
    }

    try {
      const systemEventsResponse = systemEventsResult.status === 'fulfilled' ? systemEventsResult.value : null;
      const customEventsResponse = customEventsResult.status === 'fulfilled' ? customEventsResult.value : null;
      const systemSignalsResponse = systemSignalsResult.status === 'fulfilled' ? systemSignalsResult.value : null;
      const userSignalsResponse = userSignalsResult.status === 'fulfilled' ? userSignalsResult.value : null;

      const unifiedItems = [
        ...normalizeEventItems(systemEventsResponse?.events || []),
        ...normalizeCustomEventItems(Array.isArray(customEventsResponse?.notifications) ? customEventsResponse.notifications : []),
        ...normalizeSystemSignalItems(systemSignalsResponse?.results || []),
        ...normalizeStrategySignalItems(userSignalsResponse?.data?.signals || []),
      ].filter((item) => getTimestampValue(item.timestamp) > 0);

      unifiedItems.sort((a, b) => getTimestampValue(b.timestamp) - getTimestampValue(a.timestamp));
      setItems(unifiedItems);
    } catch (err) {
      const message = err?.message || 'Failed to process activity feed';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllActivity();
  }, [fetchAllActivity]);

  const filteredItems = useMemo(() => {
    const symbolSearch = filters.symbol.trim().toUpperCase();
    const rangeStart = getRangeStart(filters.timeRange);

    return items.filter((item) => {
      const timestamp = getTimestampValue(item.timestamp);
      const sourceMatch = filters.source === 'all' || item.source === filters.source;
      const typeMatch = filters.type === 'all' || item.type === filters.type;
      const symbolMatch = !symbolSearch || String(item.symbol || '').toUpperCase().includes(symbolSearch);
      const rangeMatch = timestamp >= rangeStart;

      return sourceMatch && typeMatch && symbolMatch && rangeMatch;
    });
  }, [items, filters]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <PageHeader
          title="Activity Feed"
          subtitle="Unified stream of events, alerts, and trade signals"
        />
        <DWLFLoader size="large" />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Activity Feed"
        subtitle="Unified stream of events, alerts, and trade signals"
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select value={filters.source} label="Source" onChange={handleFilterChange('source')}>
                  {SOURCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={filters.type} label="Type" onChange={handleFilterChange('type')}>
                  {TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Range</InputLabel>
                <Select value={filters.timeRange} label="Time Range" onChange={handleFilterChange('timeRange')}>
                  {RANGE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label="Symbol"
                size="small"
                fullWidth
                value={filters.symbol}
                onChange={handleFilterChange('symbol')}
                placeholder="BTC/USD"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchAllActivity}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {filteredItems.map((item) => (
          <Paper key={item.id} sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip label={item.sourceLabel} size="small" color="secondary" />
                  <Chip label={item.type} size="small" variant="outlined" />
                  {item.symbol && <Chip label={item.symbol} size="small" color="primary" variant="outlined" />}
                </Stack>

                <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {item.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(item.timestamp)}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  component={RouterLink}
                  to={item.detailPath}
                >
                  Open Detail
                </Button>
                <Button
                  size="small"
                  variant="text"
                  component={RouterLink}
                  to={item.sourcePath}
                >
                  Source Page
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}

        {!filteredItems.length && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No activity matches the selected filters.
            </Typography>
          </Paper>
        )}
      </Stack>
    </Container>
  );
};

export default ActivityFeedPage;
